import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import SlateLogo from './SlateLogo';
import { Loader2, Mail, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

async function apiPost(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  return data;
}

export default function LoginDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [otpForm, setOtpForm] = useState({ phone: '', otp: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin ? { email: form.email, password: form.password } : form;
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(r => r.clone());
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Invalid credentials'); return; }
      login(data);
      onOpenChange(false);
      navigate('/dashboard');
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    const phone = otpForm.phone.trim();
    if (phone.length < 10) { setError('Enter a valid phone number'); return; }
    setOtpLoading(true);
    setError('');
    try {
      const data = await apiPost(`${API}/api/auth/otp/send`, { phone });
      if (data.error) { setError(data.error); return; }
      setOtpSent(true);
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    setError('');
    try {
      const data = await apiPost(`${API}/api/auth/otp/verify`, { phone: otpForm.phone.trim(), otp: otpForm.otp.trim() });
      if (!data.success) { setError(data.error || 'Verification failed'); return; }
      login(data);
      onOpenChange(false);
      navigate('/dashboard');
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-6" data-testid="login-dialog">
        <DialogTitle className="sr-only">Sign in to Slate</DialogTitle>
        <DialogDescription className="sr-only">Sign in with email, WhatsApp, or Google</DialogDescription>
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3"><SlateLogo size={28} /></div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">Welcome to Slate</h2>
          <p className="text-xs text-muted-foreground mt-1">Sign in to your account</p>
        </div>

        {error && <p className="text-destructive text-xs text-center mb-3 bg-destructive/10 rounded-lg py-2 px-3" data-testid="auth-error">{error}</p>}

        <Tabs defaultValue="email" className="w-full" onValueChange={() => setError('')}>
          <TabsList className="w-full mb-3 h-8">
            <TabsTrigger value="email" className="flex-1 text-xs gap-1"><Mail className="w-3 h-3" /> Email</TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex-1 text-xs gap-1"><Phone className="w-3 h-3" /> WhatsApp</TabsTrigger>
          </TabsList>

          <TabsContent value="email">
            <form onSubmit={handleSubmit} className="space-y-2.5">
              {!isLogin && (
                <Input placeholder="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 text-sm" data-testid="name-input" required />
              )}
              <Input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-9 text-sm" data-testid="email-input" required />
              <Input type="password" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="h-9 text-sm" data-testid="password-input" required />
              <Button type="submit" className="w-full h-9 text-sm" disabled={loading} data-testid="submit-auth-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>
            <p className="text-xs text-center text-muted-foreground mt-2">
              {isLogin ? "Don't have an account?" : 'Already have one?'}{' '}
              <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-foreground font-medium hover:underline" data-testid="toggle-auth-mode">
                {isLogin ? 'Create account' : 'Sign in'}
              </button>
            </p>
          </TabsContent>

          <TabsContent value="whatsapp">
            {!otpSent ? (
              <div className="space-y-2.5">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">+91</span>
                  <Input type="tel" placeholder="Phone number" value={otpForm.phone} onChange={e => setOtpForm({ ...otpForm, phone: e.target.value })} className="h-9 text-sm pl-11" data-testid="phone-input" maxLength={10} />
                </div>
                <Button className="w-full h-9 text-sm" onClick={handleSendOTP} disabled={otpLoading} data-testid="send-otp-btn">
                  {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send OTP via WhatsApp'}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-2.5">
                <p className="text-xs text-muted-foreground text-center">OTP sent to +91 {otpForm.phone}</p>
                <Input type="text" placeholder="Enter 6-digit OTP" value={otpForm.otp} onChange={e => setOtpForm({ ...otpForm, otp: e.target.value })} className="h-9 text-sm text-center tracking-[0.3em] font-mono" data-testid="otp-input" maxLength={6} autoFocus />
                <Button type="submit" className="w-full h-9 text-sm" disabled={otpLoading} data-testid="verify-otp-btn">
                  {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Sign In'}
                </Button>
                <button onClick={() => { setOtpSent(false); setError(''); }} className="text-xs text-muted-foreground hover:text-foreground w-full text-center" data-testid="change-phone-btn">Change number</button>
              </form>
            )}
          </TabsContent>
        </Tabs>

        <div className="relative my-3">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center"><span className="bg-background px-3 text-[9px] text-muted-foreground uppercase tracking-wider">or</span></div>
        </div>

        <Button variant="outline" className="w-full h-9 text-sm gap-2" onClick={handleGoogle} data-testid="google-login-btn">
          <svg width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </Button>
      </DialogContent>
    </Dialog>
  );
}
